import React, { useState, useEffect, useRef } from 'react';
import { backupAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    DatabaseBackup,
    Download,
    Loader2,
    RotateCcw,
    Clock,
    HardDriveDownload,
    AlertTriangle,
    Mail,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString('it-IT', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const BackupPage = () => {
    const { isAdmin } = useAuth();
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [frequency, setFrequency] = useState('weekly');
    const [savingFreq, setSavingFreq] = useState(false);
    const [emailEnabled, setEmailEnabled] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadBackups();
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const res = await backupAPI.getSettings();
            setFrequency(res.data.frequency || 'weekly');
            setEmailEnabled(!!res.data.email_enabled);
            setEmailRecipients((res.data.email_recipients || []).join(', '));
        } catch {
            /* noop */
        }
    };

    const saveEmailSettings = async (enabled, recipientsStr) => {
        const list = recipientsStr.split(',').map((s) => s.trim()).filter(Boolean);
        setSavingEmail(true);
        try {
            await backupAPI.updateEmailSettings(enabled, list);
            toast.success(enabled ? 'Invio backup via email attivato' : 'Invio backup via email disattivato');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio email'));
            loadSettings();
        } finally {
            setSavingEmail(false);
        }
    };

    const handleToggleEmail = (checked) => {
        setEmailEnabled(checked);
        saveEmailSettings(checked, emailRecipients);
    };

    const handleFrequencyChange = async (value) => {
        setFrequency(value);
        setSavingFreq(true);
        try {
            await backupAPI.updateSettings(value);
            toast.success(value === 'daily' ? 'Backup automatico ogni giorno' : 'Backup automatico ogni settimana');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        } finally {
            setSavingFreq(false);
        }
    };

    const loadBackups = async () => {
        try {
            const res = await backupAPI.list();
            setBackups(res.data);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel caricamento dei backup'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            await backupAPI.create();
            toast.success('Backup creato con successo');
            loadBackups();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore durante il backup'));
        } finally {
            setCreating(false);
        }
    };

    const handleDownload = async (backup) => {
        setDownloadingId(backup.id);
        try {
            const res = await backupAPI.download(backup.id);
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = backup.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel download'));
        } finally {
            setDownloadingId(null);
        }
    };

    const handleRestore = async () => {
        if (!restoreFile) return;
        setRestoring(true);
        try {
            const res = await backupAPI.restore(restoreFile);
            toast.success(`Ripristino completato: ${res.data.total_docs} documenti`);
            setRestoreFile(null);
            setConfirmText('');
            loadBackups();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore durante il ripristino'));
        } finally {
            setRestoring(false);
        }
    };

    const onFilePicked = (e) => {
        const f = e.target.files?.[0];
        if (f) {
            setRestoreFile(f);
            setConfirmText('');
        }
        e.target.value = '';
    };

    if (!isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="backup-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Backup Database</h1>
                    <p className="text-muted-foreground">
                        Backup automatico {frequency === 'daily' ? 'ogni giorno' : 'ogni settimana'}. Conservati gli ultimi 12.
                    </p>
                </div>
                <div className="flex gap-2">
                    <input
                        type="file"
                        accept="application/json,.json"
                        ref={fileInputRef}
                        onChange={onFilePicked}
                        className="hidden"
                        data-testid="restore-file-input"
                    />
                    <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="open-restore-btn"
                    >
                        <RotateCcw size={18} className="mr-2" />
                        RIPRISTINA
                    </Button>
                    <Button
                        className="btn-primary font-heading"
                        onClick={handleCreate}
                        disabled={creating}
                        data-testid="create-backup-btn"
                    >
                        {creating ? (
                            <><Loader2 size={18} className="mr-2 animate-spin" />BACKUP IN CORSO...</>
                        ) : (
                            <><DatabaseBackup size={18} className="mr-2" />CREA BACKUP ORA</>
                        )}
                    </Button>
                </div>
            </div>

            {/* Auto-backup info + frequency */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-muted/40 rounded-sm border text-sm">
                <div className="flex items-start gap-3 flex-1">
                    <Clock size={18} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-muted-foreground">
                        I backup vengono salvati in uno <b>storage persistente</b> esterno al server, quindi restano al sicuro
                        anche dopo riavvii o aggiornamenti dell'app. Scarica periodicamente una copia sul tuo computer.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 md:border-l md:pl-4">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Frequenza</span>
                    <Select value={frequency} onValueChange={handleFrequencyChange} disabled={savingFreq}>
                        <SelectTrigger className="w-36 h-9" data-testid="backup-frequency-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="daily">Ogni giorno</SelectItem>
                            <SelectItem value="weekly">Ogni settimana</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Invio backup via email */}
            <div className="rounded-lg border bg-card p-4" data-testid="backup-email-card">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-primary"><Mail size={20} /></div>
                        <div>
                            <p className="font-medium">Invia i backup via email</p>
                            <p className="text-sm text-muted-foreground">
                                Ad ogni backup (automatico e manuale) invia un'email con gli allegati (dati .zip + codice sorgente .zip), il riepilogo e un link sicuro per scaricare i dati (valido 14 giorni).
                            </p>
                        </div>
                    </div>
                    <Switch checked={emailEnabled} onCheckedChange={handleToggleEmail} disabled={savingEmail} data-testid="backup-email-toggle" />
                </div>
                <div className="mt-4 pl-8">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Destinatari (separati da virgola)</Label>
                    <div className="flex flex-col sm:flex-row gap-2 mt-1">
                        <Input
                            value={emailRecipients}
                            onChange={(e) => setEmailRecipients(e.target.value)}
                            placeholder="ced@pezzaioli.it, ..."
                            className="max-w-md"
                            data-testid="backup-email-recipients"
                        />
                        <Button
                            variant="outline"
                            onClick={() => saveEmailSettings(emailEnabled, emailRecipients)}
                            disabled={savingEmail}
                            data-testid="save-backup-email-btn"
                        >
                            {savingEmail ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                            Salva destinatari
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Nota: al momento l'invio funziona verso <strong>ced@pezzaioli.it</strong>. Per inviare anche ad altri indirizzi occorre verificare il dominio pezzaioli.it su Resend.
                    </p>
                </div>
            </div>

            {/* Backups list */}
            {backups.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground" data-testid="no-backups">
                    <HardDriveDownload size={40} className="mx-auto mb-3 opacity-40" />
                    <p>Nessun backup presente. Creane uno adesso.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {backups.map((b) => (
                        <Card key={b.id} className="card-hover" data-testid={`backup-row-${b.id}`}>
                            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center text-white shrink-0">
                                    <DatabaseBackup size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{b.filename}</p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                                        <span>{formatDate(b.created_at)}</span>
                                        <span>•</span>
                                        <span>{formatBytes(b.size)}</span>
                                        <span>•</span>
                                        <span>{b.docs_count} documenti</span>
                                        <Badge variant={b.created_by === 'auto' ? 'outline' : 'secondary'} className="text-xs">
                                            {b.created_by === 'auto' ? 'Automatico' : 'Manuale'}
                                        </Badge>
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownload(b)}
                                    disabled={downloadingId === b.id}
                                    data-testid={`download-backup-${b.id}`}
                                >
                                    {downloadingId === b.id ? (
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                    ) : (
                                        <Download size={16} className="mr-2" />
                                    )}
                                    Scarica
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Restore confirmation dialog */}
            <Dialog open={!!restoreFile} onOpenChange={(v) => { if (!v) { setRestoreFile(null); setConfirmText(''); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase text-destructive flex items-center gap-2">
                            <AlertTriangle size={20} /> Ripristina backup
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4" data-testid="restore-dialog">
                        <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-sm">
                            <b>Attenzione:</b> il ripristino <b>sostituisce tutti i dati attuali</b> (offerte, commesse, clienti,
                            utenti e credenziali) con quelli contenuti nel file. L'operazione non è reversibile.
                        </div>
                        <div className="text-sm">
                            File selezionato: <b className="break-all">{restoreFile?.name}</b>
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground mb-2">
                                Per confermare, scrivi <b>RIPRISTINA</b> qui sotto:
                            </p>
                            <input
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                className="w-full h-10 px-3 border rounded-sm text-sm"
                                placeholder="RIPRISTINA"
                                data-testid="restore-confirm-input"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="outline" onClick={() => { setRestoreFile(null); setConfirmText(''); }}>
                                Annulla
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleRestore}
                                disabled={confirmText !== 'RIPRISTINA' || restoring}
                                data-testid="confirm-restore-btn"
                            >
                                {restoring ? (
                                    <><Loader2 size={16} className="mr-2 animate-spin" />Ripristino...</>
                                ) : 'RIPRISTINA ORA'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BackupPage;
